import { supabase } from "../../services/SupabaseService";
import { getCurrentUserId } from "../../utils/helpers";
import { Project } from "../../entities/Project";

class ProjectsRepository {
  private getUserId(): string {
    const id = getCurrentUserId();
    if (!id) throw new Error("User not authenticated");
    return id;
  }

  async getAll(): Promise<Project[]> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("user_id", userId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(Project.fromRow);
  }

  async getById(id: string): Promise<Project | null> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("id", id)
      .eq("user_id", userId)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data ? Project.fromRow(data) : null;
  }

  /** Creates a project and adds the source language to project_languages. */
  async create(name: string, sourceLanguageCode: string): Promise<Project> {
    const userId = this.getUserId();
    const normalizedCode = sourceLanguageCode.trim().toLowerCase();

    const { data, error } = await supabase
      .from("projects")
      .insert({ name: name.trim(), source_language_code: normalizedCode, user_id: userId })
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error("Project could not be created.");
    const project = Project.fromRow(data);

    // Add source language to project_languages
    const { error: langError } = await supabase
      .from("project_languages")
      .insert({ project_id: project.id, language_code: normalizedCode, user_id: userId });

    if (langError) throw new Error(langError.message);

    return project;
  }

  async update(id: string, name: string, sourceLanguageCode: string): Promise<Project> {
    const userId = this.getUserId();
    const normalizedCode = sourceLanguageCode.trim().toLowerCase();

    const { data, error } = await supabase
      .from("projects")
      .update({ name: name.trim(), source_language_code: normalizedCode })
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error("Project not found.");
    return Project.fromRow(data);
  }

  async softDelete(id: string): Promise<void> {
    const userId = this.getUserId();
    const { error } = await supabase.from("projects").update({ deleted_at: new Date().toISOString() }).eq("id", id).eq("user_id", userId);

    if (error) throw new Error(error.message);
  }
}

export default ProjectsRepository;
